import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { ScanLine, X, CheckCircle2, AlertTriangle, Keyboard, Camera, Loader2, User, CalendarDays, Moon, Hotel } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { bookingsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/check-in")({
  component: CheckInPage,
});

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });

function CheckInPage() {
  const [mode, setMode] = useState<"scanner" | "manual">("scanner");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<{
    bookingRef: string; guestName: string; venueName: string;
    startDate: string; endDate: string; message: string; newStatus: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState("");
  const [verifying, setVerifying] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const startScanner = async () => {
    setError(null);
    setResult(null);
    setScanning(true);
    try {
      const scanner = new Html5Qrcode("qr-scanner");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          await scanner.stop();
          setScanning(false);
          await verify(decodedText.trim());
        },
        () => {}
      );
    } catch (err) {
      setScanning(false);
      setError("Camera access denied or unavailable. Use manual entry.");
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const verify = async (t: string) => {
    if (!t) return;
    setVerifying(true);
    setError(null);
    setResult(null);
    try {
      const res = await bookingsApi.verifyCheckIn(t);
      toast.success(res.message);
      setResult(res);
    } catch (err: any) {
      const msg = err?.message || "Invalid or expired token.";
      setError(msg);
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try { scannerRef.current.stop(); } catch { /* noop */ }
      }
    };
  }, []);

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="grid size-10 place-items-center rounded-xl bg-primary/10">
          <ScanLine className="size-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Guest Check-In</h1>
          <p className="text-sm text-muted-foreground">Scan a guest's QR code or enter their check-in token.</p>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="mb-4 flex gap-1 rounded-xl border border-border/60 bg-muted/40 p-1 w-fit">
        <button
          onClick={() => { setMode("scanner"); setError(null); setResult(null); }}
          className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === "scanner" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Camera className="inline size-3.5 mr-1" /> Camera
        </button>
        <button
          onClick={() => { setMode("manual"); stopScanner(); setError(null); setResult(null); }}
          className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === "manual" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Keyboard className="inline size-3.5 mr-1" /> Manual
        </button>
      </div>

      {mode === "scanner" ? (
        <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-soft">
          {!scanning && !result && !error && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ScanLine className="size-12 text-muted-foreground/30 mb-3" />
              <p className="font-semibold">Ready to scan</p>
              <p className="text-sm text-muted-foreground mt-1">Point your camera at the guest's QR code.</p>
              <Button onClick={startScanner} className="mt-4 gap-2">
                <Camera className="size-4" /> Start Camera
              </Button>
            </div>
          )}
          {scanning && (
            <div className="relative">
              <div ref={containerRef} id="qr-scanner" className="w-full rounded-xl overflow-hidden" />
              <button
                onClick={stopScanner}
                className="absolute top-2 right-2 grid size-8 place-items-center rounded-full bg-black/50 text-white hover:bg-black/70 transition"
              >
                <X className="size-4" />
              </button>
              <p className="text-center text-xs text-muted-foreground mt-2">Align QR code within the frame</p>
            </div>
          )}
          {verifying && (
            <div className="flex items-center justify-center py-12 gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-5 animate-spin" /> Verifying token…
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-soft">
          <label className="text-sm font-medium">Check-In Token</label>
          <div className="mt-2 flex gap-2">
            <Input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="e.g. CHK-A1B2C3D4E5F6"
              className="flex-1"
              onKeyDown={(e) => e.key === "Enter" && verify(token)}
            />
            <Button onClick={() => verify(token)} disabled={verifying || !token.trim()} className="gap-2">
              {verifying ? <Loader2 className="size-4 animate-spin" /> : <><CheckCircle2 className="size-4" /> Verify</>}
            </Button>
          </div>
          {verifying && (
            <div className="flex items-center justify-center py-8 gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Verifying…
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
          <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm text-destructive">Check-in failed</p>
            <p className="text-sm text-destructive/80">{error}</p>
          </div>
        </div>
      )}

      {/* Success Result */}
      {result && (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="size-5 text-emerald-600" />
            <h3 className="font-bold text-emerald-800">Check-In Successful</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <User className="size-4 text-emerald-600" />
              <span className="font-medium">{result.guestName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Hotel className="size-4 text-emerald-600" />
              <span>{result.venueName}</span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDays className="size-4 text-emerald-600" />
              <span>{fmtDate(result.startDate)} → {fmtDate(result.endDate)}</span>
            </div>
            <div className="flex items-center gap-2">
              <ScanLine className="size-4 text-emerald-600" />
              <span className="font-mono text-xs">{result.bookingRef}</span>
            </div>
          </div>
          <Button
            onClick={() => { setResult(null); setError(null); if (mode === "scanner") startScanner(); }}
            variant="outline"
            className="mt-4 w-full"
          >
            Check In Next Guest
          </Button>
        </div>
      )}
    </div>
  );
}
